import { FeatureFlagState } from "~/modules/feature_flag/types";
import { ExperimentState } from "~/modules/experiments/types";

// // // //

/**
 * UserProfileCompanyTypes
 * Enum representing the available company types for UserProfile.companyType
 */
export enum UserProfileCompanyTypes {
    None = "",
    BuySideFamilyOffice = "Buy-Side - Family Office",
    BuySideRIA = "Buy-Side - RIA",
    BuySideHedgeFund = "Buy-Side - Hedge Fund",
    BuySideAllocator = "Buy-Side - Allocator",
    BuySideCorporate = "Buy-Side - Corporate",
    BuySideIndividualInvestor = "Buy-Side - Individual Investor",
    SellSideAssetManagerIssuer = "Sell-Side - Asset Manager/Issuer",
    SellSideTokenizationPlatform = "Sell-Side - Tokenization Platform",
    SellSideExchangeTradingVenue = "Sell-Side - Exchange/Trading Venue",
    SellSideBrokerDealer = "Sell-Side - Broker/Dealer",
    SellSideMarketMaker = "Sell-Side - Market Maker",
    ServiceProvider = "Service Provider",
    BlockchainNetwork = "Blockchain Network",
    InfrastructureProvider = "Infrastructure Provider",
    Media = "Media",
    Academia = "Academia",
    RegulatorGovernment = "Regulator/Government",
    Other = "Other",
}

/**
 * UserProfileOccupations
 * Enum representing the available occupations for UserProfile.occupation
 * @deprecated This will be removed in favor of companyType
 */
export enum UserProfileOccupations {
    // NOTE - these are some old values that we uesd to use
    // Making a note of them for when we update old entries in the database
    // Protocol = "Protocol Developer",
    // Originator = "Asset Originator",
    // InvestorDebt = "Investor - Credit",
    // InvestorEquities = "Investor - Equities",
    // CryptocurrencyEnthusiast = "Cryptocurrency Enthusiast",
    // FinancialJournalist = "Journalist / Reporter",
    // Developer = "Developer",
    // PreferNotToSay = "Prefer Not To Say",
    // // // //
    None = "",
    Accountant = "Accountant",
    Analyst = "Analyst",
    AssetIssuer = "Asset Issuer",
    AssetManager = "Asset Manager",
    AssetOriginator = "Asset Originator",
    Auditor = "Auditor",
    BusinessDevelopment = "Business Development",
    CryptoEnthusiast = "Crypto Enthusiast",
    ApplicationDeveloper = "Application Developer ",
    ProtocolDeveloper = "Protocol Developer",
    FinancialAdvisor = "Financial Advisor",
    InsuranceProfessional = "Insurance Professional",
    InvestorCredit = "Investor - Credit",
    InvestorEquity = "Investor - Equity",
    InvestorRealEstate = "Investor - Real Estate",
    InvestorOther = "Investor - Other",
    JournalistReporter = "Journalist/Reporter",
    Product = "Product",
    Regulator = "Regulator",
    RiskManager = "Risk Manager",
    Trader = "Trader",
    Underwriter = "Underwriter",
    VentureCapitalist = "Venture Capitalist",
    Other = "Other",
}

/**
 * UserProfileAcquisitionChannels
 * Enum representing the various referral sources.
 */
export enum UserProfileAcquisitionChannels {
    None = "",
    NewsArticle = "News or Blog Article",
    Twitter = "Twitter",
    LinkedIn = "LinkedIn",
    SocialMedia = "Social Media",
    WordOfMouth = "Word of Mouth",
    SearchEngine = "Search Engine",
    Podcast = "Podcast",
    EventOrConference = "Event or Conference",
    GovernmentOrganization = "Government Organization",
    IndustryAssociation = "Industry Association",
    PreferNotToSay = "Prefer Not To Say",
    Other = "Other",
}

/**
 * UserProfileAssetClassInterests
 * Enum representing asset classes in which users may express interest
 */
export enum UserProfileAssetClassInterests {
    PrivateCredit = "Private Credit",
    PublicSecurities = "Public Securities",
    Stablecoins = "Stablecoins",
    RealEstate = "Real Estate",
    PrivateFunds = "Private Funds",
}

/**
 * UserPreferences
 * UserSettings.preferences value object
 */
export interface UserPreferences {
    darkMode: boolean;
    defaultOrganizationID?: string | null;
}

/**
 * UserProfile
 * UserSettings.profile value object
 */
export interface UserProfile {
    // New required fields
    company: string;
    companyType: UserProfileCompanyTypes;

    // Existing fields
    acquisitionChannel: UserProfileAcquisitionChannels;
    assetClassInterests: UserProfileAssetClassInterests[];
    shouldReceiveMarketing: boolean;

    // Deprecated field (keeping for backwards compatibility)
    occupation: UserProfileOccupations;
}

export interface User {
    id: string;
    name: string;
    email: string;
    emailVerified: any;
    image: string;
    role: "CUSTOMER" | "SUPERADMIN";
    organization: Organization;
    memberships: Membership[];
    pending_invitations: Membership[];
    profile: UserProfile;
    preferences: UserPreferences;
    features: FeatureFlagState;
    experiments: ExperimentState;
    intercom_hmac: string | null;
    createdAt: string | null;
}

export interface Membership {
    id: string;
    role: string;
    organizationID: string;
    userID: string;
    invitedName: any;
    invitedEmail: any;
    organization: Organization;
}

export enum OrganizationScope {
    SINGLEUSER = "SINGLEUSER",
    MULTIUSER = "MULTIUSER",
}

export interface Organization {
    id: string;
    name: string;
    domain: string;
    scope: OrganizationScope;
    autoEnroll: boolean;
    plan: Plan;
}

// NOTE - this must match the PlanType enum in the Prisma schema
export enum PlanTypes {
    FREE = "FREE",
    TRIAL_STARTER = "TRIAL_STARTER",
    TRIAL_ALL_ACCESS = "TRIAL_ALL_ACCESS",
    TRIAL_DATA_DOWNLOADS = "TRIAL_DATA_DOWNLOADS",
    PREMIUM_STARTER = "PREMIUM_STARTER",
    PREMIUM_ALL_ACCESS = "PREMIUM_ALL_ACCESS",
    PREMIUM_DATA_DOWNLOADS = "PREMIUM_DATA_DOWNLOADS",
    PREMIUM_ACADEMIC = "PREMIUM_ACADEMIC",
}

export enum StripePriceType {
    DOWNLOADS_50_MONTHLY = "DOWNLOADS_50_MONTHLY",
    DOWNLOADS_MONTHLY = "DOWNLOADS_MONTHLY",
    ACADEMIC_MONTHLY = "ACADEMIC_MONTHLY",
}

interface Plan {
    id: string;
    type: PlanTypes;
    organizationID: string;
    stripeCustomerID: string | null;
    stripePriceID: StripePriceType | null;
    licensedSeats: number;
    expiresAt: string | null;
}

// // // //

export interface UserStoreState {
    user: User;
    loading: boolean;
    isLoggedIn: boolean;
    error: string | null;
}
